import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import Card from '../components/Card';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import FollowUpModal, { FU_SHORT } from '../components/FollowUpModal';
import { getFollowUpsLatest } from '../services/followUpApi';
import { getReorderDue, getWinBack, getTargets, setTarget, getTrend, getDesignBuyers } from '../services/dashboardApi';

// GROWTH v1: Reorder Due / Win-back / Target vs Achievement / Design & Size trend
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TABS = [['reorder', 'Reorder Due'], ['winback', 'Win-back'], ['target', 'Target vs Achievement'], ['trend', 'Design / Size Trend'], ['buyers', 'Design / Size Buyers']];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const dmy = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const mlabel = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;
const shiftYm = (ym, d) => { const dt = new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + d, 1)); return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`; };
const tick = { fill: 'var(--color-text-secondary)', fontSize: 11 };
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };
const TF = { ...TD, fontWeight: 800, borderTop: '2px solid var(--color-border)' };
const R = { textAlign: 'right' };
const FIELD = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
const HINT = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 };
const OK = 'var(--color-success)';
const BAD = 'var(--color-danger)';
const WARN = 'var(--color-warning)';

function useLoad(fn, deps) {
  const [st, setSt] = useState({ data: null, err: '' });
  useEffect(() => {
    let alive = true;
    setSt({ data: null, err: '' });
    fn()
      .then((d) => { if (alive) setSt({ data: d, err: '' }); })
      .catch((e) => { if (alive) setSt({ data: null, err: (e && e.response && e.response.data && e.response.data.error) || 'Could not load.' }); });
    return () => { alive = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return st;
}

function Status({ st }) {
  if (st.err) return <p style={{ margin: 0, color: BAD }}>{st.err}</p>;
  if (!st.data) return <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Loading…</p>;
  return null;
}

function Chip({ label, value, color }) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', background: 'var(--color-surface)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--color-text-primary)' }}>{value}</div>
    </div>
  );
}

function Pill({ text, color }) {
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 'var(--font-size-xs)', fontWeight: 700, color, border: `1px solid ${color}` }}>{text}</span>;
}

// FOLLOW-UP v1: last call + "Log call" button for one customer row
function FollowCell({ id, name, fu, onFollow }) {
  return (
    <td style={TD} onClick={(e) => e.stopPropagation()}>
      {fu && (
        <div style={{ fontSize: 12, marginBottom: 4 }}>
          <b>{FU_SHORT[fu.outcome] || fu.outcome}</b> · {dmy(fu.date)}
          {fu.note ? <div style={{ color: 'var(--color-text-muted)' }}>{fu.note}</div> : null}
        </div>
      )}
      <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => onFollow({ id, name })}>{fu ? 'Update' : 'Log call'}</button>
    </td>
  );
}

function ReorderTab({ onOpen, onFollow, fuMap }) {
  const st = useLoad(getReorderDue, []);
  const [q, setQ] = useState('');
  const [sf, setSf] = useState('');
  const d = st.data;
  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d ? d.items : []).filter((i) => (!sf || i.status === sf) && (!s || `${i.name} ${i.city} ${i.refParties.join(' ')}`.toLowerCase().includes(s)));
  }, [d, q, sf]);
  return (
    <Card title="Reorder Due — customers who should have ordered by now">
      <Status st={st} />
      {d && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <Chip label="Overdue" value={fmt(d.overdue)} color={BAD} />
            <Chip label="Due in the next 7 days" value={fmt(d.dueSoon)} color={WARN} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / city / Ref. Party" style={{ ...FIELD, width: '100%', maxWidth: 320 }} />
            <select value={sf} onChange={(e) => setSf(e.target.value)} style={FIELD} aria-label="Status">
              <option value="">All</option><option value="Overdue">Overdue</option><option value="Due soon">Due soon</option>
            </select>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }} className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th><th style={TH}>Ref. Party</th>
                  <th style={TH}>Last dispatch</th><th style={{ ...TH, ...R }}>Usual gap</th><th style={TH}>Due date</th><th style={TH}>Status</th>
                  <th style={{ ...TH, ...R }}>Box (12 months)</th><th style={{ ...TH, ...R }}>Dispatch days</th><th style={TH}>Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td style={TD} colSpan={11}>No customer to follow up right now.</td></tr>}
                {items.map((i, n) => (
                  <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => onOpen({ id: i.id, name: i.name })} title="Click to see this customer's dispatch history">
                    <td style={TD}>{n + 1}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{i.name}</td>
                    <td style={TD}>{i.city || '—'}</td>
                    <td style={TD}>{i.refParties.join(', ') || '—'}</td>
                    <td style={TD}>{dmy(i.lastDate)} <span style={{ color: 'var(--color-text-muted)' }}>({i.daysSince}d ago)</span></td>
                    <td style={{ ...TD, ...R }}>{i.gapDays} days</td>
                    <td style={TD}>{dmy(i.dueDate)}</td>
                    <td style={TD}>
                      {i.status === 'Overdue' ? <Pill text={`Overdue ${i.daysLate}d`} color={BAD} /> : <Pill text={i.daysLeft > 0 ? `Due in ${i.daysLeft}d` : 'Due now'} color={WARN} />}
                    </td>
                    <td style={{ ...TD, ...R, fontWeight: 700 }}>{fmt(i.boxes12m)}</td>
                    <td style={{ ...TD, ...R }}>{i.dispatchDays}</td>
                    <FollowCell id={i.id} name={i.name} fu={fuMap && fuMap[i.id]} onFollow={onFollow} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={HINT}>
            Usual gap = the typical number of days between this customer&apos;s dispatches in the last 12 months (needs at least 3 dispatch days).
            Customers silent for 90+ days are on the Win-back tab. Biggest buyers come first.
          </div>
        </>
      )}
    </Card>
  );
}

function WinBackTab({ onOpen, onFollow, fuMap }) {
  const st = useLoad(getWinBack, []);
  const [q, setQ] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const d = st.data;
  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d ? d.items : []).filter((i) => (!pendingOnly || !i.reason) && (!s || `${i.name} ${i.city} ${i.reason || ''} ${i.refParties.join(' ')}`.toLowerCase().includes(s)));
  }, [d, q, pendingOnly]);
  return (
    <Card title="Win-back — customers silent for 90+ days, biggest buyers first">
      <Status st={st} />
      {d && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <Chip label="Customers to win back" value={fmt(d.count)} />
            <Chip label="Box they bought earlier" value={fmt(d.totalBoxes)} color="var(--color-accent)" />
            <Chip label="Reason still pending" value={fmt(d.reasonPending)} color={d.reasonPending > 0 ? BAD : OK} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / city / reason" style={{ ...FIELD, width: '100%', maxWidth: 320 }} />
            <label style={{ fontSize: 'var(--font-size-sm)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} /> Only reason pending
            </label>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }} className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th><th style={TH}>Ref. Party</th>
                  <th style={{ ...TH, ...R }}>Total box bought</th><th style={{ ...TH, ...R }}>Dispatch days</th><th style={{ ...TH, ...R }}>Avg box / dispatch</th>
                  <th style={TH}>Last dispatch</th><th style={TH}>Reason</th><th style={TH}>Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td style={TD} colSpan={10}>No customer found.</td></tr>}
                {items.map((i, n) => (
                  <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => onOpen({ id: i.id, name: i.name })} title="Click to see this customer's dispatch history">
                    <td style={TD}>{n + 1}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{i.name}</td>
                    <td style={TD}>{i.city || '—'}</td>
                    <td style={TD}>{i.refParties.join(', ') || '—'}</td>
                    <td style={{ ...TD, ...R, fontWeight: 800 }}>{fmt(i.boxes)}</td>
                    <td style={{ ...TD, ...R }}>{i.dispatchDays}</td>
                    <td style={{ ...TD, ...R }}>{fmt(i.avgPerDispatch)}</td>
                    <td style={TD}>{dmy(i.lastDate)} <span style={{ color: 'var(--color-text-muted)' }}>({i.daysSince}d)</span></td>
                    <td style={{ ...TD, color: i.reason ? 'var(--color-text-primary)' : BAD, fontWeight: i.reason ? 400 : 700 }}>{i.reason || 'Reason pending'}</td>
                    <FollowCell id={i.id} name={i.name} fu={fuMap && fuMap[i.id]} onFollow={onFollow} />
                  </tr>
                ))}
              </tbody>
              {items.length > 0 && (
                <tfoot><tr><td style={TF} colSpan={4}>Total ({items.length} customers)</td><td style={{ ...TF, ...R }}>{fmt(items.reduce((a, i) => a + i.boxes, 0))}</td><td style={TF} colSpan={5} /></tr></tfoot>
              )}
            </table>
          </div>
          <div style={HINT}>Ranked by total box bought from you (most first). Reasons are the ones written on the Inactive Customers page.</div>
        </>
      )}
    </Card>
  );
}

function TargetCell({ row, ym, canEdit, onSaved }) {
  const [v, setV] = useState(row.target == null ? '' : String(row.target));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setV(row.target == null ? '' : String(row.target)); }, [row.target, ym]);
  if (!canEdit) return <>{row.target == null ? <span style={{ color: 'var(--color-text-muted)' }}>Not set</span> : fmt(row.target)}</>;
  const save = async () => {
    const clean = v.trim() === '' ? null : Number(v);
    if (clean === (row.target == null ? null : row.target)) return;
    setBusy(true); setErr('');
    try { await setTarget({ refPartyId: row.id, month: ym, target: clean }); onSaved(); } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.error) || 'Could not save');
    } finally { setBusy(false); }
  };
  return (
    <span>
      <input
        value={v} disabled={busy} inputMode="numeric" aria-label={`Target for ${row.name}`}
        onChange={(e) => setV(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder="set"
        style={{ ...FIELD, width: 110, padding: '4px 8px', textAlign: 'right' }}
      />
      {err && <div style={{ color: BAD, fontSize: 'var(--font-size-xs)' }}>{err}</div>}
    </span>
  );
}

function TargetTab() {
  const [month, setMonth] = useState('');
  const [bump, setBump] = useState(0);
  const st = useLoad(() => getTargets(month || undefined), [month, bump]);
  const d = st.data;
  const thisYm = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const tot = useMemo(() => {
    if (!d) return null;
    const t = d.rows.filter((r) => r.target != null).reduce((a, r) => a + r.target, 0);
    return { ach: d.rows.reduce((a, r) => a + r.achieved, 0), prev: d.rows.reduce((a, r) => a + r.prevAchieved, 0), target: t, hasTarget: d.rows.some((r) => r.target != null) };
  }, [d]);
  const pctOf = (a, t) => (t > 0 ? Math.round((a / t) * 1000) / 10 : null);
  const colorOf = (p) => (p == null ? 'var(--color-text-muted)' : p >= 100 ? OK : p >= 70 ? WARN : BAD);
  const chg = (cur, prev) => {
    if (prev > 0) { const p = ((cur - prev) / prev) * 100; return <span style={{ color: p >= 0 ? OK : BAD, fontWeight: 700 }}>{p >= 0 ? '▲' : '▼'} {Math.abs(p).toFixed(1)}%</span>; }
    return cur > 0 ? <span style={{ color: OK, fontWeight: 700 }}>New</span> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  };
  return (
    <>
      <Card title={`Target vs Achievement${d ? ` — ${mlabel(d.ym)}` : ''}`} style={{ marginBottom: 'var(--space-4)' }}>
        <Status st={st} />
        {d && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setMonth(shiftYm(d.ym, -1))}>◀ Previous</button>
              <button type="button" className="btn btn-secondary" onClick={() => setMonth(shiftYm(d.ym, 1))} disabled={d.ym >= thisYm}>Next ▶</button>
              {month && <button type="button" className="btn btn-secondary" onClick={() => setMonth('')}>This month</button>}
            </div>
            {!d.targetsReady && <p style={{ color: BAD }}>Target table is not created yet. Ask the admin to run the setup SQL for targets.</p>}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>#</th><th style={TH}>Ref. Party</th>
                    <th style={{ ...TH, ...R }}>Target (box)</th><th style={{ ...TH, ...R }}>Achieved (box)</th>
                    <th style={{ ...TH, minWidth: 160 }}>% of target</th>
                    <th style={{ ...TH, ...R }}>Last month ({mlabel(d.prevYm)})</th><th style={{ ...TH, ...R }}>vs last month</th><th style={{ ...TH, ...R }}>Last month target %</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => {
                    const p = pctOf(r.achieved, r.target);
                    const pp = pctOf(r.prevAchieved, r.prevTarget);
                    return (
                      <tr key={r.id}>
                        <td style={TD}>{i + 1}</td>
                        <td style={{ ...TD, fontWeight: 600 }}>{r.name}</td>
                        <td style={{ ...TD, ...R }}><TargetCell row={r} ym={d.ym} canEdit={d.canEdit && d.targetsReady} onSaved={() => setBump((x) => x + 1)} /></td>
                        <td style={{ ...TD, ...R, fontWeight: 800 }}>{fmt(r.achieved)}</td>
                        <td style={TD}>
                          {p == null ? <span style={{ color: 'var(--color-text-muted)' }}>—</span> : (
                            <div>
                              <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(100, p)}%`, height: '100%', background: colorOf(p) }} />
                              </div>
                              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: colorOf(p) }}>{p}%</div>
                            </div>
                          )}
                        </td>
                        <td style={{ ...TD, ...R }}>{fmt(r.prevAchieved)}</td>
                        <td style={{ ...TD, ...R }}>{chg(r.achieved, r.prevAchieved)}</td>
                        <td style={{ ...TD, ...R, color: colorOf(pp), fontWeight: 700 }}>{pp == null ? '—' : `${pp}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {d.rows.length > 1 && (
                  <tfoot>
                    <tr>
                      <td style={TF} colSpan={2}>Total</td>
                      <td style={{ ...TF, ...R }}>{tot.hasTarget ? fmt(tot.target) : '—'}</td>
                      <td style={{ ...TF, ...R }}>{fmt(tot.ach)}</td>
                      <td style={{ ...TF, color: colorOf(pctOf(tot.ach, tot.target)) }}>{tot.hasTarget && tot.target > 0 ? `${pctOf(tot.ach, tot.target)}%` : '—'}</td>
                      <td style={{ ...TF, ...R }}>{fmt(tot.prev)}</td>
                      <td style={{ ...TF, ...R }}>{chg(tot.ach, tot.prev)}</td>
                      <td style={TF} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div style={HINT}>{d.canEdit ? 'Admin: type a target in the box and press Enter (leave empty to remove it).' : 'Targets are set by the admin.'} Achieved = box dispatched in the month.</div>
          </>
        )}
      </Card>
      {d && (
        <Card title="Last 6 months — target vs achieved" style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={d.history.map((h) => ({ label: mlabel(h.ym), Achieved: h.achieved, Target: h.target }))} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} />
                <YAxis tick={tick} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Target" fill="#8F9AA6" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Bar dataKey="Achieved" fill="#0E6F8A" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </>
  );
}

function TrendTab() {
  const st = useLoad(getTrend, []);
  const [q, setQ] = useState('');
  const [sizeSel, setSizeSel] = useState('');
  const d = st.data;
  const chgCell = (x) => (x.isNew ? <span style={{ color: OK, fontWeight: 700 }}>New</span>
    : x.stopped ? <span style={{ color: BAD, fontWeight: 700 }}>Stopped</span>
      : <span style={{ color: x.change >= 0 ? OK : BAD, fontWeight: 700 }}>{x.change >= 0 ? '▲' : '▼'} {Math.abs(x.pct).toFixed(1)}%</span>);
  const designs = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d ? d.designs : []).filter((x) => (!sizeSel || x.size === sizeSel) && (!s || x.name.toLowerCase().includes(s)));
  }, [d, q, sizeSel]);
  const rising = designs.filter((x) => x.change > 0).sort((a, b) => b.change - a.change).slice(0, 15);
  const falling = designs.filter((x) => x.change < 0).sort((a, b) => a.change - b.change).slice(0, 15);
  const designTable = (title, list, empty) => (
    <Card title={title} style={{ marginBottom: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>#</th><th style={TH}>Design</th><th style={TH}>Size</th><th style={{ ...TH, ...R }}>Last 90 days</th><th style={{ ...TH, ...R }}>Before that</th><th style={{ ...TH, ...R }}>Change</th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td style={TD} colSpan={6}>{empty}</td></tr>}
            {list.map((x, i) => (
              <tr key={`${x.id}-${x.size}`}>
                <td style={TD}>{i + 1}</td><td style={{ ...TD, fontWeight: 600 }}>{x.name}</td><td style={TD}>{x.size}</td>
                <td style={{ ...TD, ...R, fontWeight: 700 }}>{fmt(x.cur)}</td><td style={{ ...TD, ...R }}>{fmt(x.prev)}</td>
                <td style={{ ...TD, ...R }}>{chgCell(x)} <span style={{ color: 'var(--color-text-muted)' }}>({x.change > 0 ? '+' : ''}{fmt(x.change)})</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
  return (
    <>
      <Status st={st} />
      {d && (
        <>
          <Card title="Size-wise — last 90 days vs the 90 days before" style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={TH}>Size</th><th style={{ ...TH, ...R }}>Last 90 days (box)</th><th style={{ ...TH, ...R }}>Before that (box)</th><th style={{ ...TH, ...R }}>Change</th></tr></thead>
                <tbody>
                  {d.sizes.length === 0 && <tr><td style={TD} colSpan={4}>No dispatch.</td></tr>}
                  {d.sizes.map((x) => (
                    <tr key={x.id} style={{ cursor: 'pointer', background: sizeSel === x.name ? 'var(--color-accent-soft)' : undefined }} onClick={() => setSizeSel(sizeSel === x.name ? '' : x.name)} title="Click to filter the designs below by this size">
                      <td style={{ ...TD, fontWeight: 600 }}>{x.name}</td>
                      <td style={{ ...TD, ...R, fontWeight: 700 }}>{fmt(x.cur)}</td><td style={{ ...TD, ...R }}>{fmt(x.prev)}</td>
                      <td style={{ ...TD, ...R }}>{chgCell(x)} <span style={{ color: 'var(--color-text-muted)' }}>({x.change > 0 ? '+' : ''}{fmt(x.change)})</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={HINT}>Click a size to see only its designs below. Designs with fewer than 10 box in 6 months are left out.</div>
          </Card>
          {d.designsAvailable ? (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)', alignItems: 'center' }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search design" style={{ ...FIELD, width: '100%', maxWidth: 280 }} />
                {sizeSel && <button type="button" className="btn btn-secondary" onClick={() => setSizeSel('')}>Size: {sizeSel} ✕</button>}
              </div>
              {/* TREND-STACK v1: Rising on top, Falling below, each full width */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)' }}>
                {designTable('Rising designs (top 15)', rising, 'No rising design.')}
                {designTable('Falling designs (top 15)', falling, 'No falling design.')}
              </div>
            </>
          ) : <p style={{ color: 'var(--color-text-muted)' }}>Design-wise data is not available.</p>}
        </>
      )}
    </>
  );
}

// DESIGN-BUYERS v1
function BuyersTab({ onOpen }) {
  const [draft, setDraft] = useState('');
  const [design, setDesign] = useState('');
  const [sizeId, setSizeId] = useState('');
  const [months, setMonths] = useState('12');
  const [st, setSt] = useState({ data: null, err: '', busy: true });
  useEffect(() => {
    let off = false;
    setSt((s) => ({ ...s, busy: true, err: '' }));
    getDesignBuyers({ design, sizeId, months })
      .then((data) => { if (!off) setSt({ data, err: '', busy: false }); })
      .catch(() => { if (!off) setSt((s) => ({ ...s, err: 'Could not load the buyers list.', busy: false })); });
    return () => { off = true; };
  }, [design, sizeId, months]);
  const d = st.data;
  const searched = Boolean(design || sizeId);
  const names = (d && d.designNames) || [];
  const sizes = (d && d.sizes) || [];
  return (
    <Card title="Design / Size Buyers — who bought this design?">
      <form
        onSubmit={(e) => { e.preventDefault(); setDesign(draft.trim()); }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}
      >
        <input list="buyers-design-list" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Design name (e.g. MELODY ASH)" style={{ ...FIELD, width: '100%', maxWidth: 300 }} />
        <datalist id="buyers-design-list">{names.map((n) => <option key={n} value={n} />)}</datalist>
        <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} style={FIELD} aria-label="Size">
          <option value="">All sizes</option>
          {sizes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={months} onChange={(e) => setMonths(e.target.value)} style={FIELD} aria-label="Period">
          <option value="3">Last 3 months</option><option value="6">Last 6 months</option><option value="12">Last 12 months</option>
          <option value="24">Last 24 months</option><option value="0">All time</option>
        </select>
        <button type="submit" className="btn btn-primary">Show buyers</button>
        {searched && <button type="button" className="btn btn-secondary" onClick={() => { setDraft(''); setDesign(''); setSizeId(''); }}>Clear</button>}
      </form>
      {st.err && <p style={{ color: BAD }}>{st.err}</p>}
      {st.busy && !d && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {d && !d.designsAvailable && <p style={{ color: 'var(--color-text-muted)' }}>Design-wise data is not available.</p>}
      {d && d.designsAvailable && !searched && <p style={{ color: 'var(--color-text-muted)' }}>Type a design name and/or choose a size, then press “Show buyers”.</p>}
      {d && d.designsAvailable && searched && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <Chip label="Customers" value={fmt(d.count)} color={OK} />
            <Chip label="Total box" value={fmt(d.totalBoxes)} color={WARN} />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }} className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th><th style={TH}>Ref. Party</th>
                  <th style={{ ...TH, ...R }}>Box</th><th style={{ ...TH, ...R }}>Dispatch days</th><th style={TH}>Last order</th><th style={TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.items.length === 0 && <tr><td style={TD} colSpan={8}>No customer found for this selection.</td></tr>}
                {d.items.map((i, n) => (
                  <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => onOpen({ id: i.id, name: i.name })} title="Click to see this customer's dispatch history">
                    <td style={TD}>{n + 1}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{i.name}</td>
                    <td style={TD}>{i.city || '—'}</td>
                    <td style={TD}>{i.refParties.join(', ') || '—'}</td>
                    <td style={{ ...TD, ...R, fontWeight: 700 }}>{fmt(i.boxes)}</td>
                    <td style={{ ...TD, ...R }}>{i.dispatchDays}</td>
                    <td style={TD}>{dmy(i.lastDate)} <span style={{ color: 'var(--color-text-muted)' }}>({i.daysSince}d ago)</span></td>
                    <td style={TD}>{i.daysSince <= 90 ? <Pill text="Active" color={OK} /> : <Pill text={`Silent ${i.daysSince}d`} color={BAD} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={HINT}>
            Shows customers who bought a design whose name contains the text you typed (all its colour/size variants), in the chosen size and period. Biggest buyers first.
            {d.truncated ? ` Showing the top ${d.items.length} of ${fmt(d.count)} customers.` : ''}
          </div>
        </>
      )}
    </Card>
  );
}

export default function GrowthPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some((t) => t[0] === sp.get('tab')) ? sp.get('tab') : 'reorder';
  const [hist, setHist] = useState(null);
  const [fuTarget, setFuTarget] = useState(null); // FOLLOW-UP v1
  const [fuMap, setFuMap] = useState({});
  const [fuBump, setFuBump] = useState(0);
  useEffect(() => {
    let off = false;
    getFollowUpsLatest().then((m) => { if (!off) setFuMap(m || {}); }).catch(() => { /* the tabs still work without it */ });
    return () => { off = true; };
  }, [fuBump]);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <h1 style={{ margin: 0 }}>Growth Tools</h1>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        {TABS.map(([k, label]) => (
          <button key={k} type="button" className={`btn ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSp({ tab: k }, { replace: true })}>{label}</button>
        ))}
      </div>
      {tab === 'reorder' && <ReorderTab onOpen={setHist} onFollow={setFuTarget} fuMap={fuMap} />}
      {tab === 'winback' && <WinBackTab onOpen={setHist} onFollow={setFuTarget} fuMap={fuMap} />}
      {fuTarget && <FollowUpModal customer={fuTarget} onClose={() => setFuTarget(null)} onSaved={() => setFuBump((b) => b + 1)} />}
      {tab === 'target' && <TargetTab />}
      {tab === 'trend' && <TrendTab />}
      {tab === 'buyers' && <BuyersTab onOpen={setHist} />}
      {hist && <CustomerHistoryModal customer={hist} onClose={() => setHist(null)} />}
    </div>
  );
}
